import { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Styling for the mammoth-converted document HTML. Tailwind's preflight strips
 * default table/heading styles, and mammoth emits no inline styles, so we scope
 * document-like typography to `.docx-preview` here.
 */
const DOCX_STYLES = `
.docx-preview { color: #1a1a1a; font-family: "Calibri", "Segoe UI", system-ui, sans-serif; font-size: 11pt; line-height: 1.5; }
.docx-preview p { margin: 0 0 8pt; }
.docx-preview h1, .docx-preview h2, .docx-preview h3, .docx-preview h4 { font-weight: 700; line-height: 1.3; margin: 16pt 0 8pt; }
.docx-preview h1 { font-size: 16pt; text-align: center; }
.docx-preview h2 { font-size: 13pt; }
.docx-preview h3 { font-size: 12pt; }
.docx-preview table { border-collapse: collapse; width: 100%; margin: 10pt 0; }
.docx-preview td, .docx-preview th { border: 1px solid #999; padding: 4pt 6pt; vertical-align: top; text-align: left; }
.docx-preview th { background: #f3f4f6; font-weight: 700; }
.docx-preview ul, .docx-preview ol { margin: 0 0 8pt; padding-left: 24pt; }
.docx-preview img { max-width: 100%; height: auto; }
.docx-preview a { color: #2563eb; text-decoration: underline; }
`;

/**
 * Renders a .docx document (converted to HTML on the server via mammoth) as a
 * styled, page-like preview. Unlike the PDF preview this mirrors the source Word
 * document directly, so it never inherits LibreOffice's pagination quirks (e.g.
 * tables jumping to the next page). The trade-off: it is a continuous flow with
 * no page boundaries. The HTML is sanitized with DOMPurify before rendering.
 */
export function DocxPreview({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  const clean = useMemo(() => DOMPurify.sanitize(html), [html]);

  return (
    <div className={className ?? "h-full w-full overflow-auto bg-muted"}>
      <style>{DOCX_STYLES}</style>
      <div
        className="docx-preview mx-auto my-6 max-w-3xl bg-white p-12 shadow-sm"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </div>
  );
}
